// Fichas por situação (Aromático / Tópico / Interno) da aba
// "Protocolos de Óleos Essenciais".
//
// Texto transcrito do arquivo protocolos_oleos_completo.md, sem reescrita.
// É só apresentação: as fichas de origem (PROTOCOLOS_OLEOS, OutrasOpcoes)
// continuam intactas.

export interface SituacaoFicha {
  situacao: string
  aromatico?: string
  topico?: string
  interno?: string
  // Uso que não é nem aromático, tópico nem interno (ex.: gota pura no local)
  nota?: string
}

export interface FichaSituacoes {
  // Vale para todo uso Tópico desta ficha. Vazio em roll-ons já diluídos.
  diluicao?: string
  // Orientação geral de aplicação (roll-ons)
  comoAplicar?: string
  situacoes: SituacaoFicha[]
  atencao?: string
}

// Instrução padrão de uso interno com água
const INTERNO_AGUA = 'pingue 1 gota em uma colher e tome com água. Se preferir, use uma cápsula vegetal.'

export const FICHAS_SITUACOES: Record<string, FichaSituacoes> = {
  // ─── Os 14 de adulto ───────────────────────────────────────────────────────

  Lavanda: {
    situacoes: [
      {
        situacao: 'Sono e insônia',
        aromatico: 'inale 1 gota nas mãos antes de dormir.',
        interno:
          '1 gota sublingual antes de dormir em casos de insônia ou tensão intensa, deixar 1 minuto sob a língua e engolir.',
      },
      {
        situacao: 'Ansiedade, estresse e desconforto emocional',
        aromatico: 'inale 1 gota nas mãos sempre que sentir desconforto.',
        topico: '1 gota + 10 de óleo de coco fracionado, no peito, nuca, pulsos e/ou sola dos pés.',
      },
      {
        situacao: 'Pele sensível',
        topico: '1 gota + 10 de óleo de coco fracionado, na área.',
      },
      {
        situacao: 'Cortes leves',
        nota: 'pode aplicar 1 gota pura para estancar o sangramento.',
      },
    ],
  },

  'Hortelã-Pimenta': {
    situacoes: [
      {
        situacao: 'Energia, foco e disposição',
        aromatico: 'inale 1 gota nas mãos.',
      },
      {
        situacao: 'Dor de cabeça e dores musculares',
        topico: '1 gota + 10 de óleo de coco fracionado, na área afetada.',
      },
      {
        situacao: 'Febre',
        topico: '1 gota + 10 de óleo de coco fracionado, na sola dos pés.',
      },
      {
        situacao: 'Desconforto digestivo',
        interno: INTERNO_AGUA,
      },
    ],
    atencao:
      'nunca usar em epiléticos e lactantes. Usar com cautela e com orientação profissional em hipertensos e gestantes.',
  },

  'Limão Siciliano': {
    situacoes: [
      {
        situacao: 'Foco, clareza mental e energia',
        aromatico: 'inale 1 gota.',
      },
      {
        situacao: 'Imunidade e detox',
        interno:
          'pingue 1 gota em uma colher e tome com água, após o café da manhã, 2 a 3 vezes ao dia durante 7 dias. Se preferir, use uma cápsula vegetal. Usar garrafa de vidro, nunca plástico.',
      },
    ],
    atencao: 'não aplicar na pele antes de exposição ao sol (causa manchas).',
  },

  Olíbano: {
    situacoes: [
      {
        situacao: 'Inflamação, memória e equilíbrio emocional',
        interno:
          '1 gota sublingual 2 a 3 vezes ao dia após as refeições. Potencializa junto com Copaíba.',
      },
      {
        situacao: 'Pele e manchas',
        topico: '1 gota + 10 de óleo de coco fracionado, na área afetada.',
      },
    ],
  },

  Copaíba: {
    situacoes: [
      {
        situacao: 'Inflamação, dores crônicas, ansiedade e sistema nervoso',
        interno:
          '1 gota sublingual 2 a 3 vezes ao dia após as refeições, deixar 1 minuto sob a língua e engolir. Potencializa junto com Olíbano.',
      },
      {
        situacao: 'Área afetada',
        topico: '1 gota + 10 de óleo de coco fracionado.',
      },
    ],
  },

  Tangerina: {
    situacoes: [
      {
        situacao: 'Alegria e calma',
        aromatico: 'inale 1 gota.',
      },
      {
        situacao: 'Sono (noite repousante)',
        interno:
          'pingue 1 gota em uma colher e tome com água, após o jantar. Se preferir, use uma cápsula vegetal.',
      },
      {
        situacao: 'Imunidade',
        interno:
          'pingue 1 gota em uma colher e tome com água, 2 a 3 vezes ao dia. Se preferir, use uma cápsula vegetal.',
      },
    ],
    atencao: 'não aplicar na pele antes de exposição ao sol (causa manchas).',
  },

  Melaleuca: {
    situacoes: [
      {
        situacao: 'Pele sensível e acne',
        topico:
          '1 gota + 10 de óleo de coco fracionado, na área afetada. Acne ou fungo de unha: 1 gota pura sobre o local.',
      },
      {
        situacao: 'Inflamações e infecções',
        interno:
          'pingue 1 gota em uma colher e tome com água, 3 vezes ao dia por até 10 dias. Se preferir, use uma cápsula vegetal.',
      },
    ],
  },

  'Mix para Estabilidade': {
    situacoes: [
      {
        situacao: 'Ansiedade, medo, raiva e estagnação',
        topico:
          '1 a 2 gotas direto na nuca, área do coração ou pulsos. Já contém óleo de coco fracionado. Se preferir diluir mais: 1 gota + 5 de óleo de coco fracionado.',
      },
    ],
    atencao: 'nunca ingerir.',
  },

  'Mix Respiratório': {
    situacoes: [
      {
        situacao: 'Problemas respiratórios',
        aromatico: 'inale 1 gota, 2 a 3 vezes ao dia.',
        topico: '1 gota + 10 de óleo de coco fracionado, na garganta, peito ou costas.',
      },
      {
        situacao: 'Ansiedade e tristeza',
        aromatico: 'inale 1 gota, 2 a 3 vezes ao dia.',
      },
    ],
    atencao: 'nunca ingerir.',
  },

  'Mix para Tensão e Dores': {
    situacoes: [
      {
        situacao: 'Dores musculares, tensão e articulações',
        topico: '1 gota + 10 de óleo de coco fracionado, na área afetada.',
      },
      {
        situacao: 'Dor de cabeça e enxaqueca',
        topico: '1 gota + 10 de óleo de coco fracionado, nas têmporas, nuca e ombros.',
      },
    ],
    atencao: 'nunca ingerir. Nunca aplicar em feridas abertas.',
  },

  'Mix Imunidade': {
    situacoes: [
      {
        situacao: 'Imunidade',
        interno:
          'pingue 1 gota em uma colher e tome com água, após a refeição, 1 vez ao dia. Se preferir, use uma cápsula vegetal.',
      },
      {
        situacao: 'Infecções e resfriado',
        interno: 'mesma forma, 3 vezes ao dia por 7 dias.',
      },
      {
        situacao: 'Proteção na pele',
        topico:
          '1 gota + 10 a 20 de óleo de coco fracionado, na sola dos pés, preferencialmente fora do sol.',
      },
      {
        situacao: 'Aromático',
        nota: 'somente no difusor.',
      },
    ],
    atencao: 'nunca inalar puro nas mãos (contém canela, pode irritar as narinas).',
  },

  'Mix Digestivo': {
    situacoes: [
      {
        situacao: 'Gases, refluxo, constipação, diarreia, cólicas e náusea',
        interno:
          'pingue 1 gota em uma colher e tome com água, 2 a 3 vezes ao dia após as refeições. Se preferir, use uma cápsula vegetal.',
        topico: '1 gota + 10 de óleo de coco fracionado, no abdômen.',
      },
    ],
  },

  'Mix Metabólico': {
    situacoes: [
      {
        situacao: 'Metabolismo, controle de apetite e energia',
        interno:
          'pingue 1 gota em uma colher e tome com água, 30 minutos antes do café da manhã e do almoço e no final da tarde. Se preferir, use uma cápsula vegetal. Garrafa de vidro, nunca plástico.',
      },
      {
        situacao: 'Gordura localizada',
        topico:
          '1 gota + 10 de óleo de coco fracionado, em massagem circular no abdômen, flancos e coxas, por 2 a 3 minutos até absorver.',
      },
    ],
    atencao: 'contém cítricos. Não aplicar na pele antes de exposição ao sol.',
  },

  'Mix Mensal Feminino': {
    situacoes: [
      {
        situacao: 'Equilíbrio emocional',
        aromatico: 'aplique nas palmas das mãos, espalhe e inale por 1 minuto.',
      },
      {
        situacao: 'TPM e cólicas',
        topico:
          'aplique no abdômen inferior com massagem suave. Para minimizar sensibilidade, pode misturar com óleo de coco fracionado.',
      },
      {
        situacao: 'Desequilíbrio hormonal',
        topico: 'nuca, têmporas, pulsos, tornozelos e sola dos pés, 2 a 3 vezes ao dia.',
      },
      {
        situacao: 'Ondas de calor',
        topico: 'nuca e pulsos ao sentir o calor.',
      },
    ],
    atencao:
      'pode ser usado o mês todo. Já vem em roll-on. Não usar durante a gravidez sem orientação médica.',
  },

  // ─── Complementares que já existiam ────────────────────────────────────────

  bergamota: {
    situacoes: [
      {
        situacao: 'Ansiedade e estresse, tristeza, autoestima e equilíbrio emocional',
        aromatico: 'inale 1 gota nas mãos por 1 a 2 minutos.',
        nota: 'Difusor: 3 a 4 gotas.',
      },
      {
        situacao: 'Pele',
        topico: '1 gota + 10 de óleo de coco fracionado, nos pulsos, peito e nuca.',
      },
    ],
    atencao: 'fotossensível, não aplicar na pele antes do sol, aguardar 12 horas.',
  },

  salvia_esclareia: {
    situacoes: [
      {
        situacao: 'Equilíbrio hormonal e emocional',
        aromatico: 'inale 1 gota nas mãos por 1 minuto.',
        topico:
          '1 gota + 10 de óleo de coco fracionado, no abdômen inferior, nuca, pulsos e tornozelos.',
      },
    ],
    atencao: 'nunca usar durante a gravidez.',
  },

  // Ficha única do Gerânio (substitui a da Etapa 1)
  geranio: {
    diluicao: '1 gota para 10 a 20 gotas de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Equilíbrio emocional, ansiedade e autoestima',
        aromatico: 'nas mãos em concha.',
        topico: 'pulsos, abdômen e nuca.',
      },
      {
        situacao: 'TPM e equilíbrio hormonal',
        topico: 'abdômen.',
        interno: INTERNO_AGUA,
      },
      {
        situacao: 'Pele e cabelo',
        topico: 'couro cabeludo ou pele; ou 1 gota no hidratante.',
      },
    ],
  },

  elevation: {
    situacoes: [
      {
        situacao: 'Tristeza, apatia e baixa energia',
        aromatico: 'inale 1 gota nas mãos pela manhã.',
        topico: '1 gota + 10 de óleo de coco fracionado, nos pulsos, peito e nuca.',
        nota: 'Difusor: 3 a 4 gotas de dia.',
      },
    ],
    atencao: 'uso preferencialmente diurno, não usar perto da hora de dormir. Fotossensível.',
  },

  // ─── Complementares com ficha nova ─────────────────────────────────────────

  vetiver: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      { situacao: 'Foco', topico: 'nuca, coluna, sob o nariz.' },
      { situacao: 'Aprendizagem', topico: 'nuca e/ou sob o nariz.' },
      { situacao: 'Tristeza e ansiedade', topico: 'com melissa, nuca, testa e pulsos.' },
      { situacao: 'Insônia e irritabilidade', topico: 'sola dos pés ou pescoço.' },
      { situacao: 'Tristeza pós-parto', topico: 'nuca, sob o nariz, pulsos.' },
    ],
  },

  serenity: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Sono',
        aromatico: 'nas mãos em concha ou difusor.',
        topico: 'sob o nariz ou sola dos pés.',
      },
      {
        situacao: 'Rotina de banho e de dormir',
        topico: 'sola dos pés. Pode usar em banhos.',
      },
      {
        situacao: 'Estresse e ansiedade',
        aromatico: 'difusor.',
        topico: 'sob o nariz e nuca.',
      },
      {
        situacao: 'Medos e nervosismo',
        aromatico: 'nas mãos em concha ou difusor.',
        topico: 'nuca.',
      },
      {
        situacao: 'Tensão e alterações de humor',
        aromatico: 'nas mãos em concha ou difusor.',
        topico: 'nuca ou peito.',
      },
      {
        situacao: 'Raiva e irritabilidade',
        aromatico: 'difusor.',
        topico: 'sob o nariz e/ou sola dos pés.',
      },
      { situacao: 'Tensão muscular', topico: 'na área.' },
    ],
  },

  adaptiv: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Estresse e ansiedade',
        aromatico: 'difusor ou nas mãos.',
        topico: 'sola dos pés.',
      },
      {
        situacao: 'Medo e raiva',
        aromatico: 'difusor.',
        topico: 'sob o nariz ou nuca.',
      },
      {
        situacao: 'Humor',
        aromatico: 'inale ou difusor.',
        topico: 'sob o nariz.',
      },
      {
        situacao: 'Meditação',
        aromatico: 'inale ou difusor.',
        topico: 'sob o nariz ou testa.',
      },
      { situacao: 'Sono', aromatico: 'difusor.' },
      { situacao: 'Dor de cabeça', topico: 'têmporas.' },
    ],
  },

  cedro: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Calmante e ansiedade',
        aromatico: '1 gota na palma das mãos e inspire.',
        topico: 'na nuca ou na sola dos pés.',
      },
      {
        situacao: 'Foco e concentração',
        aromatico: 'difusor ou inale.',
        topico: 'atrás do pescoço e na testa.',
      },
      {
        situacao: 'Tosse e seios paranasais',
        aromatico: 'inale das mãos em concha.',
        topico: 'no peito e na testa.',
      },
      {
        situacao: 'Acne',
        topico: 'com 1 gota de Melaleuca nas manchas.',
      },
    ],
    atencao: 'Nunca ingerir.',
  },

  ddr_prime: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Inflamações, infecções e processos autoimunes',
        interno: 'opcional, com água ou em cápsula.',
        topico: 'nuca, coluna ou sola dos pés.',
      },
    ],
    atencao: 'ciclos de 30 dias de uso e 10 dias de pausa.',
  },

  intune: {
    comoAplicar:
      'aplique diretamente nos locais indicados (nuca, pulsos, sob o nariz, coluna e sola dos pés). Se preferir mais suave, aplique no local e depois acrescente 5 gotas de óleo de coco fracionado por cima.',
    situacoes: [
      { situacao: 'Foco e concentração', topico: 'sob o nariz e nuca.' },
      { situacao: 'Acalmar e aterrar', topico: 'sob o nariz, nuca e pontos de pulsação.' },
      { situacao: 'Nervosismo e ansiedade', topico: 'sob o nariz, sola dos pés e nuca.' },
      {
        situacao: 'Estresse e hiperatividade',
        topico: 'parte de trás do pescoço e pontos de pulsação.',
      },
      {
        situacao: 'Tristeza de fim de tarde',
        aromatico: 'inale das mãos em concha.',
        topico: 'sob o nariz e/ou nuca.',
      },
      {
        situacao: 'Equilíbrio da atividade mental',
        topico: 'parte posterior do pescoço ou da coluna.',
      },
    ],
  },

  zendocrine: {
    diluicao: '1 gota + 10 de óleo de coco fracionado',
    situacoes: [
      {
        situacao: 'Limpeza e desintoxicação (rim, fígado, vesícula)',
        topico: 'nos pontos de pulsação e na nuca.',
        interno: INTERNO_AGUA,
      },
      {
        situacao: 'Perda de peso e desintoxicação',
        interno: INTERNO_AGUA,
      },
      {
        situacao: 'Prisão de ventre',
        interno: 'mesma forma.',
        topico: 'sobre o abdômen.',
      },
      {
        situacao: 'Fadiga e exaustão',
        interno: 'mesma forma.',
        topico: 'no abdômen, nuca ou sola dos pés.',
      },
      {
        situacao: 'Desequilíbrio hormonal',
        interno: 'mesma forma.',
        topico: 'no abdômen ou na sola dos pés.',
      },
      {
        situacao: 'Ressaca',
        interno: 'mesma forma.',
        topico: 'na sola dos pés.',
      },
    ],
  },
}

// Lista curta das situações, para o campo "Indicado"
export function indicadoDaFicha(ficha: FichaSituacoes): string {
  return `${ficha.situacoes.map((s) => s.situacao).join(', ')}.`
}

// Linhas de uma situação: Aromático / Tópico / Interno / observação.
// A diluição da ficha acompanha cada uso tópico que ainda não a traz no texto.
export function linhasDaSituacao(ficha: FichaSituacoes, situacao: SituacaoFicha): string[] {
  const linhas: string[] = []
  if (situacao.aromatico) linhas.push(`Aromático: ${situacao.aromatico}`)
  if (situacao.topico) {
    const jaTemDiluicao = /óleo de coco fracionado/i.test(situacao.topico)
    const diluicao =
      ficha.diluicao && !jaTemDiluicao ? ` Diluição: ${ficha.diluicao}.` : ''
    linhas.push(`Tópico: ${situacao.topico}${diluicao}`)
  }
  if (situacao.interno) linhas.push(`Ingerir: ${situacao.interno}`)
  if (situacao.nota) linhas.push(situacao.nota)
  return linhas
}
